import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  canonical?: string;
  schema?: object;
  ogImage?: string;
}

const DEFAULT_TITLE = 'Manlab Medicals Incorporated | Medical Supplies & Equipment Zimbabwe';
const DEFAULT_DESC = 'Leading distributor of medical supplies, electrosurgical equipment, and laboratory solutions in Zimbabwe and across Africa. High-quality hospital equipment and reagents.';
const SITE_DOMAIN = 'https://www.manlab.co.zw';

export const useSEO = ({ 
  title, 
  description, 
  canonical, 
  schema,
  ogImage 
}: SEOProps) => {
  useEffect(() => {
    // 1. Title
    document.title = title ? `${title} | Manlab Medicals` : DEFAULT_TITLE;

    // 2. Meta Description
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute('content', description || DEFAULT_DESC);
    }

    // 3. Open Graph / Twitter Descriptions
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', title || DEFAULT_TITLE);

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', description || DEFAULT_DESC);
    
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', title || DEFAULT_TITLE);

    const twDesc = document.querySelector('meta[property="twitter:description"]');
    if (twDesc) twDesc.setAttribute('content', description || DEFAULT_DESC);

    // 4. OG Image
    if (ogImage) {
      const ogImg = document.querySelector('meta[property="og:image"]');
      if (ogImg) ogImg.setAttribute('content', ogImage);
      const twImg = document.querySelector('meta[property="twitter:image"]');
      if (twImg) twImg.setAttribute('content', ogImage);
    }

    // 5. Canonical Link
    let canonicalTag = document.querySelector('link[rel="canonical"]');
    const canonicalUrl = canonical ? `${SITE_DOMAIN}${canonical}` : SITE_DOMAIN;
    if (canonicalTag) {
      canonicalTag.setAttribute('href', canonicalUrl);
    } else {
      canonicalTag = document.createElement('link');
      canonicalTag.setAttribute('rel', 'canonical');
      canonicalTag.setAttribute('href', canonicalUrl);
      document.head.appendChild(canonicalTag);
    }

    // 6. JSON-LD Schema
    let schemaScript = document.getElementById('dynamic-json-ld');
    if (schema) {
      if (!schemaScript) {
        schemaScript = document.createElement('script');
        schemaScript.id = 'dynamic-json-ld';
        schemaScript.setAttribute('type', 'application/ld+json');
        document.head.appendChild(schemaScript);
      }
      schemaScript.textContent = JSON.stringify(schema);
    } else if (schemaScript) {
      // Clear if no schema provided for this page
      schemaScript.textContent = '';
    }

    // Cleanup: We don't necessarily want to revert everything immediately 
    // because the next page will call this hook again.
  }, [title, description, canonical, schema, ogImage]);
};
